import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  ScheduleException,
  ScheduleExceptionFilter,
  ScheduleExceptionListResponse,
  CreateScheduleExceptionDto,
  UpdateScheduleExceptionDto,
} from '@/types/schedule.types';

/**
 * Get list of schedule exceptions with filters
 */
export const useScheduleExceptions = (params: ScheduleExceptionFilter = {}) => {
  return useQuery<ScheduleExceptionListResponse>({
    queryKey: ['scheduleExceptions', params],
    queryFn: async () => {
      const response = await api.get('/api/v1/schedule-exceptions', {
        params: {
          page: params.page || 1,
          limit: params.limit || 20,
          employeeId: params.employeeId,
          dateFrom: params.dateFrom,
          dateTo: params.dateTo,
          sort: params.sort,
        },
      });
      return {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 20,
      };
    },
  });
};

/**
 * Get single schedule exception by ID
 */
export const useScheduleException = (id: string | null) => {
  return useQuery<ScheduleException>({
    queryKey: ['scheduleException', id],
    queryFn: async () => {
      if (!id) throw new Error('Exception ID is required');
      const response = await api.get(`/api/v1/schedule-exceptions/${id}`);
      return response.data || response;
    },
    enabled: !!id,
  });
};

/**
 * Create new schedule exception
 */
export const useCreateScheduleException = () => {
  const queryClient = useQueryClient();

  return useMutation<ScheduleException, Error, CreateScheduleExceptionDto>({
    mutationFn: async (data: CreateScheduleExceptionDto) => {
      const response = await api.post('/api/v1/schedule-exceptions', data);
      return response.data || response;
    },
    onSuccess: () => {
      // Invalidate exceptions list
      queryClient.invalidateQueries({ queryKey: ['scheduleExceptions'] });
      // Invalidate department schedules that might be affected
      queryClient.invalidateQueries({ queryKey: ['departmentSchedule'] });
      // Invalidate resolved schedules
      queryClient.invalidateQueries({ queryKey: ['resolvedSchedule'] });
    },
  });
};

/**
 * Update schedule exception
 */
export const useUpdateScheduleException = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ScheduleException,
    Error,
    { id: string; data: UpdateScheduleExceptionDto }
  >({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch(`/api/v1/schedule-exceptions/${id}`, data);
      return response.data || response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['scheduleExceptions'] });
      queryClient.invalidateQueries({ queryKey: ['scheduleException', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['departmentSchedule'] });
      queryClient.invalidateQueries({ queryKey: ['resolvedSchedule'] });
    },
  });
};

/**
 * Delete schedule exception
 */
export const useDeleteScheduleException = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/schedule-exceptions/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduleExceptions'] });
      queryClient.invalidateQueries({ queryKey: ['departmentSchedule'] });
      queryClient.invalidateQueries({ queryKey: ['resolvedSchedule'] });
    },
  });
};

/**
 * Get exceptions for a specific employee in date range
 * Useful for calendar views
 */
export const useEmployeeExceptions = (
  employeeId: string | null,
  dateFrom: string,
  dateTo: string,
) => {
  return useQuery<ScheduleExceptionListResponse>({
    queryKey: ['employeeExceptions', employeeId, dateFrom, dateTo],
    queryFn: async () => {
      if (!employeeId) throw new Error('Employee ID is required');
      const response = await api.get('/api/v1/schedule-exceptions', {
        params: {
          employeeId,
          dateFrom,
          dateTo,
          limit: 100, // Get all exceptions in range
        },
      });
      return {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 100,
      };
    },
    enabled: !!employeeId && !!dateFrom && !!dateTo,
  });
};
